/**
 * Re-interpret a stored instant's WALL-CLOCK in a different timezone.
 *
 * The event form stores UTC instants but authors them as a wall-clock typed in
 * some zone. When the admin changes the zone, the wall-clock is what they mean
 * to keep: an event typed as 6 PM while the zone said Toronto, then switched to
 * Vancouver, is 6 PM Vancouver — a DIFFERENT instant, three hours later.
 *
 * The alternative (hold the instant, re-render the clock as 3 PM) would leave
 * the pickers showing a time the admin never typed, and would make the zone
 * select a no-op on the payload. Calendar apps hold the wall-clock for the same
 * reason.
 *
 * Pure and framework-agnostic — the platform's own date library does the
 * conversion via the injected `convert`, so `common/` stays free of dayjs and
 * both platforms share the rule.
 */

export interface RebaseInstantDeps {
  /**
   * Read a UTC ISO instant as a wall-clock in `timezone`, as plain numbers.
   * Returns null when the ISO cannot be parsed.
   */
  readWallClock: (
    iso: string,
    timezone: string
  ) => {
    year: number
    month: number
    day: number
    hour: number
    minute: number
    second: number
  } | null
  /** Write a wall-clock back to a UTC ISO instant, interpreted in `timezone`. */
  writeWallClock: (
    wallClock: {
      year: number
      month: number
      day: number
      hour: number
      minute: number
      second: number
    },
    timezone: string
  ) => string
}

/**
 * `iso` re-interpreted so its wall-clock in `fromZone` becomes the same
 * wall-clock in `toZone`.
 *
 * Returns the input untouched when there is nothing to rebase — an empty value,
 * a missing zone on either side, or the two zones already matching — so callers
 * can apply it unconditionally.
 */
export const rebaseInstantToZone = (
  iso: string,
  fromZone: string,
  toZone: string,
  deps: RebaseInstantDeps
): string => {
  if (!iso || !fromZone || !toZone || fromZone === toZone) {
    return iso
  }

  const wallClock = deps.readWallClock(iso, fromZone)

  if (!wallClock) {
    return iso
  }

  return deps.writeWallClock(wallClock, toZone)
}
